import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PeopleIcon from '@mui/icons-material/People';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { db } from '../../lib/firebase.js';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';

const COLORS = ['#007FFF', '#4CAF50', '#FF9800', '#9C27B0', '#E91E63'];

function StatCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'primary' }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {trend === 'up' ? (
                  <TrendingUpIcon sx={{ color: 'success.main', fontSize: 18, mr: 0.5 }} />
                ) : (
                  <TrendingDownIcon sx={{ color: 'error.main', fontSize: 18, mr: 0.5 }} />
                )}
                <Typography
                  variant="body2"
                  sx={{ color: trend === 'up' ? 'success.main' : 'error.main' }}
                >
                  {trendValue}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main` }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalStories: 0,
    totalCharacters: 0,
    storiesThisMonth: 0,
    storiesLastMonth: 0,
    usersByTier: [],
    recentActivity: [],
    storiesOverTime: [],
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all stories
      const storiesSnapshot = await getDocs(collection(db, 'stories'));
      const stories = storiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Fetch all characters
      const charactersSnapshot = await getDocs(collection(db, 'characters'));
      const characters = charactersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Get unique users from stories and characters
      const userIds = new Set([
        ...stories.map(s => s.user_id),
        ...characters.map(c => c.user_id),
      ].filter(Boolean));

      // Calculate stories this month vs last month
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      const storiesThisMonth = stories.filter(s => {
        const date = new Date(s.created_at);
        return date >= thisMonthStart;
      }).length;

      const storiesLastMonth = stories.filter(s => {
        const date = new Date(s.created_at);
        return date >= lastMonthStart && date < thisMonthStart;
      }).length;

      // Calculate stories over time (last 7 days)
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        const nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);

        const count = stories.filter(s => {
          const storyDate = new Date(s.created_at);
          return storyDate >= date && storyDate < nextDate;
        }).length;

        last7Days.push({
          date: date.toLocaleDateString('en-US', { weekday: 'short' }),
          stories: count,
        });
      }

      // Get recent stories for activity feed
      const recentStories = stories
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5)
        .map(s => ({
          id: s.id,
          type: 'story',
          title: s.title || 'Untitled Story',
          userId: s.user_id,
          date: s.created_at,
        }));

      // Calculate image generation stats
      const imagesGenerated = stories.reduce((acc, story) => {
        const pageImages = (story.pages || []).filter(p => p.imageUrl).length;
        const hasCover = story.cover_image_url ? 1 : 0;
        return acc + pageImages + hasCover;
      }, 0);

      // Calculate audio usage
      const storiesWithAudio = stories.filter(s => s.selected_voice).length;

      setStats({
        totalUsers: userIds.size,
        totalStories: stories.length,
        totalCharacters: characters.length,
        storiesThisMonth,
        storiesLastMonth,
        imagesGenerated,
        storiesWithAudio,
        recentActivity: recentStories,
        storiesOverTime: last7Days,
        usersByTier: [
          { name: 'Free', value: Math.floor(userIds.size * 0.7), color: '#90CAF9' },
          { name: 'Basic', value: Math.floor(userIds.size * 0.15), color: '#4CAF50' },
          { name: 'Premium', value: Math.floor(userIds.size * 0.1), color: '#FF9800' },
          { name: 'Premium+', value: Math.floor(userIds.size * 0.05), color: '#9C27B0' },
        ],
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const storyTrend = stats.storiesThisMonth >= stats.storiesLastMonth ? 'up' : 'down';
  const trendPercent = stats.storiesLastMonth > 0
    ? Math.round(((stats.storiesThisMonth - stats.storiesLastMonth) / stats.storiesLastMonth) * 100)
    : 100;

  return (
    <Box>
      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            subtitle="Active accounts"
            icon={PeopleIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Stories"
            value={stats.totalStories}
            subtitle={`${stats.storiesThisMonth} this month`}
            icon={AutoStoriesIcon}
            trend={storyTrend}
            trendValue={`${trendPercent >= 0 ? '+' : ''}${trendPercent}% vs last month`}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Characters Created"
            value={stats.totalCharacters}
            subtitle={`${(stats.totalCharacters / Math.max(stats.totalUsers, 1)).toFixed(1)} per user`}
            icon={PersonIcon}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Images Generated"
            value={stats.imagesGenerated}
            subtitle={`${stats.storiesWithAudio} audio stories`}
            icon={ImageIcon}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: 350 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Stories Generated (Last 7 Days)
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <AreaChart data={stats.storiesOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="stories"
                  stroke="#007FFF"
                  fill="#007FFF"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 350 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Users by Tier
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <PieChart>
                <Pie
                  data={stats.usersByTier}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {stats.usersByTier.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value, entry) => (
                    <span style={{ color: '#333', fontSize: 12 }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Recent Stories
            </Typography>
            <List>
              {stats.recentActivity.map((activity, index) => (
                <React.Fragment key={activity.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.light' }}>
                        <AutoStoriesIcon color="primary" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={activity.title}
                      secondary={new Date(activity.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    />
                    <Chip
                      label="Story"
                      size="small"
                      sx={{ bgcolor: 'primary.light', color: 'primary.main' }}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
              {stats.recentActivity.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No recent activity
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Quick Stats
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                    {stats.totalStories > 0
                      ? (stats.imagesGenerated / stats.totalStories).toFixed(1)
                      : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Images per Story
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                    {stats.totalUsers > 0
                      ? (stats.totalStories / stats.totalUsers).toFixed(1)
                      : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Stories per User
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                    {stats.storiesWithAudio}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Stories with Audio
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="secondary.main" sx={{ fontWeight: 700 }}>
                    {stats.totalCharacters}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Characters
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AdminDashboard;
